import { Component, Input, OnInit } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { AZUtils } from '../../common/utils';

@Component({
  selector: 'app-purchase-stock-mapping-modal',
  templateUrl: './purchase-stock-mapping.modal.html',
  styleUrls: ['./purchase-stock-mapping.modal.scss'],
})
export class PurchaseStockMappingModalComponent implements OnInit {
  @Input() prod_id: string = '';
  @Input() prod_line_id: string = '';
  @Input() firm_id: string = '';

  isLoading: boolean = true;

  // Production line details
  line: any = null;

  // Current mappings
  mappings: any[] = [];
  total_mapped: number = 0;

  // Add mapping form
  showAdd: boolean = false;
  flag_direct_po: boolean = true;
  purchase_lines: any[] = [];
  purchasePickerOpen: boolean = false;
  purchasePickerSearch: string = '';

  selected_pur_line_id: string = '';
  selected_pur_id: string = '';
  available_qty: number = 0;
  stock_age: number = 0;
  selectedPurchaseLine: any = null;

  qty: string = '';
  over_head: string = '';
  notes: string = '';

  constructor(
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private mutils: AZUtils,
  ) { }

  ngOnInit() { }

  async ionViewWillEnter() {
    await this.loadAll();
  }

  async loadAll() {
    try {
      this.isLoading = true;
      await this.loadLineDetails();
      await this.loadMappings();
      await this.loadPurchaseLines();
    } finally {
      this.isLoading = false;
    }
  }

  async loadLineDetails() {
    let mxdata = await this.mutils.postData('getProdLineForMapping', {
      prod_id: this.prod_id,
      prod_line_id: this.prod_line_id
    });
    this.mutils.dismissLoader();
    if (mxdata.header.status == 1) {
      this.line = mxdata.body.data.data;
    } else {
      this.mutils.presentToast(mxdata.body.error.msg || 'Failed to load line');
    }
  }

  async loadMappings() {
    let mxdata = await this.mutils.postData('getCurrentStockMappingsProd', {
      prod_id: this.prod_id,
      prod_line_id: this.prod_line_id
    });
    this.mutils.dismissLoader();
    if (mxdata.header.status == 1) {
      this.mappings = mxdata.body.data.mappings || [];
      this.total_mapped = parseFloat(mxdata.body.data.total_mapped || 0);
    } else {
      this.mappings = [];
      this.total_mapped = 0;
      this.mutils.presentToast(mxdata.body.error.msg || 'Failed to load mappings');
    }
  }

  async loadPurchaseLines() {
    if (!this.line?.mat_id) return;
    // firm_id omitted — Admin production_ajax passes empty firm_id (cross-firm purchases)
    let mxdata = await this.mutils.postData('getPurchaseLinesCombinedProd', {
      mat_id: this.line.mat_id,
      flag_direct_po: this.flag_direct_po ? 1 : 0,
      proc_sp_id: this.line?.proc_sp_id || '',
    });
    this.mutils.dismissLoader();
    if (mxdata.header.status == 1) {
      this.purchase_lines = mxdata.body.data.lines || [];
    } else {
      this.purchase_lines = [];
      this.mutils.presentToast(mxdata.body.error.msg || 'Failed to load purchases');
    }
  }

  get prodQty(): number {
    return parseFloat(this.line?.prod_qty || 0);
  }

  get remainingQty(): number {
    return Math.max(0, this.prodQty - (parseFloat(this.total_mapped as any) || 0));
  }

  openPurchasePicker() {
    this.purchasePickerSearch = '';
    this.purchasePickerOpen = true;
  }

  closePurchasePicker() {
    this.purchasePickerOpen = false;
  }

  get filteredPurchaseLines(): any[] {
    const term = (this.purchasePickerSearch || '').trim().toLowerCase();
    if (!term) return this.purchase_lines || [];
    return (this.purchase_lines || []).filter((x: any) => {
      const hay = `${x.mat_name || ''} ${this.line?.mat_name || ''} ${x.pur_number || ''} ${x.pur_date || ''} ${x.line_number || ''} ${x.available_qty || ''} ${x.uom_name || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }

  selectPurchaseLine(row: any) {
    if (!row) return;
    this.selected_pur_line_id = row.pur_line_id;
    this.selected_pur_id = row.pur_id;
    this.available_qty = parseFloat(row.available_qty || 0);
    this.stock_age = parseInt(row.stock_age || 0);
    this.selectedPurchaseLine = row;

    const suggested = Math.min(this.remainingQty, this.available_qty);
    this.qty = suggested > 0 ? suggested.toFixed(2) : '';

    this.closePurchasePicker();
  }

  async toggleAdd() {
    this.showAdd = !this.showAdd;
    if (this.showAdd) {
      await this.loadPurchaseLines();
    } else {
      this.resetForm();
    }
  }

  resetForm() {
    this.selected_pur_line_id = '';
    this.selected_pur_id = '';
    this.available_qty = 0;
    this.stock_age = 0;
    this.selectedPurchaseLine = null;
    this.qty = '';
    this.over_head = '';
    this.notes = '';
  }

  async addMapping() {
    const qtyNum = parseFloat(this.qty || '0');
    const ohNum = parseFloat(this.over_head || '0') || 0;

    if (!this.selected_pur_line_id || !this.selected_pur_id) {
      this.mutils.presentToast('Please select a purchase line');
      return;
    }
    if (!qtyNum || qtyNum <= 0) {
      this.mutils.presentToast('Please enter valid quantity');
      return;
    }
    if (qtyNum > this.available_qty) {
      this.mutils.presentToast(`Qty cannot exceed available (${this.available_qty.toFixed(2)})`);
      return;
    }
    if (qtyNum > this.remainingQty) {
      this.mutils.presentToast(`Qty cannot exceed remaining (${this.remainingQty.toFixed(2)})`);
      return;
    }

    let mxdata = await this.mutils.postData('addStockMappingProd', {
      prod_id: this.prod_id,
      prod_line_id: this.prod_line_id,
      pur_id: this.selected_pur_id,
      pur_line_id: this.selected_pur_line_id,
      mat_id: this.line.mat_id,
      qty: qtyNum,
      over_head: ohNum,
      notes: (this.notes || '').trim(),
    });
    this.mutils.dismissLoader();

    if (mxdata.header.status == 1) {
      this.mutils.presentToast('Mapping added');
      this.showAdd = false;
      this.resetForm();
      await this.loadMappings();
    } else {
      this.mutils.presentToast(mxdata.body.error.msg || 'Failed to add mapping');
    }
  }

  async deleteMapping(m: any) {
    const alert = await this.alertCtrl.create({
      header: 'Delete Mapping',
      message: `Delete mapping for ${m?.pur_number || 'purchase'}?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            let mxdata = await this.mutils.postData('deleteStockMappingProd', { mapping_id: m.pur_stock_map_id });
            this.mutils.dismissLoader();
            if (mxdata.header.status == 1) {
              this.mutils.presentToast('Mapping deleted');
              await this.loadMappings();
            } else {
              this.mutils.presentToast(mxdata.body.error.msg || 'Failed to delete');
            }
          }
        }
      ]
    });
    await alert.present();
  }

  close() {
    this.modalCtrl.dismiss({ refresh: true });
  }

  stockAgeColor(age: number): string {
    if (age > 30) return 'danger';
    if (age > 15) return 'warning';
    return 'success';
  }
}

